import React, { useEffect, useMemo, useState } from "react";
import {
  BaseModal,
  BaseModalBody,
  BaseModalHeader,
  Checkbox,
  DateBasedPaginator,
  Loading,
} from "@src/common/components";
import classNames from "classnames";
import { ShipmentRow } from "@src/shipments/types";
import {
  getShipmentTableColumns,
  TableColumnType,
} from "./ShipmentTableColumns";
import { TableContainer } from "@src/common/components";
import SortingOptionsDropdown from "./SortingOptionsDropdown";
import { SORTED_COLUMN_BG } from "@src/common/lib/tableUtils";
import { useHeaderSorting } from "@src/common/hooks/useHeaderSorting";
import { sortFields } from "../constants/sortFields";
import PrimaryFilters from "./PrimaryFilters";
import { DriverFilterSelector } from "./DriverFilterSelector";
import { useShipmentFiltersContext } from "../context/ShipmentFiltersContext";
import { useShipmentsCoreDataContext } from "../context/ShipmentsCoreData";
import TableFilterPopover from "@src/common/components/TableFilterPopover";
import { useTableControl } from "../context/TableControlContext";
import OrdersFilter from "./OrdersFilter";
import OrderRow from "./OrderRow";
import SearchShipmentsInput from "@src/common/components/SearchShipmentsInput/SearchShipmentsInput";
import { LayersManager } from "baseui/layer";
import ShipmentsTableFooter from "./ShipmentsTableFooter";
import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/24/outline";
import {
  PackageTypeCategory,
  ShipmentsSorting,
  SortingDirection,
} from "@api/graphql/generated/generated-types";
import RoutingJobsStatusButtons from "./RoutingJobsStatusButtons";
import { useShipmentSelectionContext } from "../context/ShipmentSelectionContext";
import CheckedShipmentsActions from "./CheckedShipmentsActions";
import FileUploaderOrderCreate from "@src/orders/create/components/FileUploaderOrderCreate";
import { SavedViewsDropdown } from "@src/common/components/SavedViews";
import { RoutesAttentionBanner } from "./RoutesAttentionBanner";
import { useFilterSync } from "../hooks/useFilterSync";
import { ShipmentRowShortcutListener } from "./ShipmentRowShortcutListener";
import { ShipmentRowActionPalette } from "./ShipmentRowActionPalette";
import { BulkActionPalette } from "./BulkActionPalette";
import { BulkShipmentShortcutListener } from "./BulkShipmentShortcutListener";
import { useAuth } from "@src/common/hooks/useAuth";
import { OrderIdentifierColumnType } from "@api/graphql/generated/generated-types";
import { useShipmentCheckToggle } from "../hooks/useShipmentCheckToggle";

type Props = {};

function ShipmentsTable({}: Props) {
  // Sync filter values from ShipmentFiltersContext to TableControlContext.filters
  // This ensures TableFilterPopover shows correct badge even when OrdersFilter isn't mounted
  useFilterSync();

  const {
    filters,
    sortFields: sortFieldsTableControl,
    setSortFields,
  } = useTableControl();
  const { courier } = useAuth();
  const orderIdentifierColumnType =
    courier?.courier?.orderIdentifierColumnType ??
    OrderIdentifierColumnType.OrderId;
  const columns = getShipmentTableColumns(orderIdentifierColumnType);
  const [instructionModalOpen, setInstructionModalOpen] = useState(false);
  const [instructions, setInstructions] = useState<string | undefined>("");
  const [showSelectAllButton, setShowSelectAllButton] = useState(false);
  const { handleSortingClick, areFieldsSorted } = useHeaderSorting();
  const { searchTerm, setSearchTerm } = useShipmentFiltersContext();
  const {
    minDate,
    setMinDate,
    maxDate,
    setMaxDate,
    selectedDriverIdFilter,
    setSelectedDriverIdFilter,
  } = useShipmentFiltersContext();

  const { rows, fetchShipments, shipmentsApiResponse, shipmentsApiLoading } =
    useShipmentsCoreDataContext();

  const { setCheckedShipments, checkedShipments } =
    useShipmentSelectionContext();

  const skidPackageTypeIds: ReadonlySet<string> = useMemo(
    () =>
      new Set(
        (courier?.courier?.packageTypes ?? [])
          .filter((packageType) => packageType.category === PackageTypeCategory.Skid)
          .map((packageType) => packageType.id),
      ),
    [courier?.courier?.packageTypes],
  );
  const handleToggleShipmentCheck = useShipmentCheckToggle();

  useEffect(() => {
    if (checkedShipments.length === rows.length) {
      setShowSelectAllButton(true);
    } else {
      setShowSelectAllButton(false);
    }
  }, [checkedShipments]);

  const closeInstructionModal = () => {
    setInstructions("");
    setInstructionModalOpen(false);
  };

  const onSortClick = (column: TableColumnType) => {
    const shipmentsSorting = sortFieldsTableControl as ShipmentsSorting[];
    const sortedField = shipmentsSorting.find(
      (sortField) => sortField.field === column.sortFields![0]
    );
    if (sortedField) {
      if (sortedField.direction === SortingDirection.Asc) {
        handleSortingClick(SortingDirection.Desc, column, true);
      } else {
        setSortFields([]);
      }
    } else {
      handleSortingClick(SortingDirection.Asc, column, true);
    }
  };

  const toggleAllCheckboxes = () => {
    if (checkedShipments.length === rows.length) {
      setCheckedShipments([]);
      setShowSelectAllButton(false);
    } else {
      const shipmentIds = rows
        .map((row) => row?.key)
        .filter(Boolean) as string[];
      setCheckedShipments(shipmentIds);
      setShowSelectAllButton(true);
    }
  };

  const isCheckedAll = () => {
    if (checkedShipments.length === 0) return false;
    return checkedShipments.length === rows.length;
  };

  const RenderSortIcon = (column: TableColumnType) => {
    const shipmentsSorting = sortFieldsTableControl as ShipmentsSorting[];
    const sortedField = shipmentsSorting.find(
      (sortField) => sortField.field === column.sortFields![0]
    );
    if (sortedField?.direction === SortingDirection.Asc) {
      return <ArrowUpIcon className="h-4 w-4 ml-2" />;
    } else {
      return <ArrowDownIcon className="h-4 w-4 ml-2" />;
    }
  };

  return (
    <>
      <ShipmentRowShortcutListener />
      <BulkShipmentShortcutListener />
      <ShipmentRowActionPalette />
      <BulkActionPalette />
      <BaseModal isOpen={instructionModalOpen} onClose={closeInstructionModal}>
        <BaseModalHeader>Instruction</BaseModalHeader>
        <BaseModalBody>{instructions}</BaseModalBody>
      </BaseModal>
      <TableContainer className="grid grid-rows-[auto,1fr,50px,auto] h-full">
        <div className="flex-init overflow-x-auto w-full">
          <div className="flex flex-col overflow-x-auto py-1 mt-2 justify-between">
            <div className="flex justify-between ">
              <PrimaryFilters />
              <div className="px-4 flex gap-2 items-center">
                <SavedViewsDropdown />
                <FileUploaderOrderCreate
                  onUploadComplete={() => fetchShipments()}
                />
              </div>
            </div>
          </div>
          <div className="p-2 flex items-center justify-between overflow-x-auto">
            <div className="flex gap-2">
              {checkedShipments.length > 0 ? (
                <CheckedShipmentsActions
                  showSelectAllButton={showSelectAllButton}
                />
              ) : (
                <>
                  <SortingOptionsDropdown sortFieldOptions={sortFields} />
                  <TableFilterPopover filters={filters} autoFocus={false}>
                    <OrdersFilter />
                  </TableFilterPopover>
                  <div className="w-48">
                    <LayersManager zIndex={200}>
                      <DriverFilterSelector
                        driverId={selectedDriverIdFilter}
                        setDriverId={setSelectedDriverIdFilter}
                        minDate={minDate}
                        maxDate={maxDate}
                      />
                    </LayersManager>
                  </div>
                  <div className="w-48">
                    <SearchShipmentsInput
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="min-w-max flex gap-2 items-center">
              <div className="">
                <RoutingJobsStatusButtons />
              </div>
              <LayersManager zIndex={200}>
                <DateBasedPaginator
                  minDate={minDate}
                  setMinDate={setMinDate}
                  maxDate={maxDate}
                  setMaxDate={setMaxDate}
                />
              </LayersManager>
            </div>
          </div>
        </div>
        <Loading loading={shipmentsApiLoading} className="grow overflow-x-auto">
          {shipmentsApiResponse?.shipments?.edges?.length !== 0 &&
          !shipmentsApiLoading ? (
            <table className="w-full relative">
              <tbody>
                <tr className="sticky top-0 bg-white dark:bg-gray-800 z-10">
                  <th className="bg-slate-50 dark:bg-gray-700 text-xs font-semibold uppercase text-gray-800 dark:text-gray-200 p-2 relative">
                    <Checkbox
                      checked={isCheckedAll()}
                      className="form-checkbox h-5 w-5 left-3 top-1.5 absolute"
                      onChange={() => {
                        toggleAllCheckboxes();
                      }}
                    />
                  </th>
                  {columns.map((column: TableColumnType, i) => (
                    <th
                      className={classNames({
                        "bg-slate-50 dark:bg-gray-700 text-xs font-semibold uppercase text-gray-800 dark:text-gray-200 py-2":
                          true,
                        truncate: true,
                        [SORTED_COLUMN_BG]:
                          column.sortFields &&
                          areFieldsSorted(column.sortFields),
                      })}
                      key={column.key}
                    >
                      <div
                        className={classNames({
                          flex: true,
                          "hover:underline hover:cursor-pointer":
                            !!column.sortFields,
                          "px-4": true,
                        })}
                        onClick={() => column.sortFields && onSortClick(column)}
                      >
                        <span className="my-auto h-full">{column.title}</span>
                        {column.sortFields &&
                          areFieldsSorted(column.sortFields) &&
                          RenderSortIcon(column)}
                      </div>
                    </th>
                  ))}
                </tr>
              </tbody>
              {rows.map((row: ShipmentRow) => (
                <OrderRow
                  row={row}
                  key={row?.key}
                  shipmentId={row?.key}
                  skidPackageTypeIds={skidPackageTypeIds}
                  orderIdentifierColumnType={orderIdentifierColumnType}
                  onToggleCheck={handleToggleShipmentCheck}
                />
              ))}
            </table>
          ) : (
            <div
              className="w-full text-center text-gray-400 dark:text-gray-500 p-4 flex items-center justify-center"
              style={{ height: 200 }}
            >
              <span>No orders found with provided filters. 🐝 </span>
            </div>
          )}
        </Loading>
        <div>{!shipmentsApiLoading && <ShipmentsTableFooter />}</div>
        <RoutesAttentionBanner />
      </TableContainer>
    </>
  );
}

export default ShipmentsTable;
